import { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, GridProps, styled, Collapse } from "@mui/material";
import NavigationBar, { NavigationFooter, NavigationItem } from "@jbhi-fi/lanyard-ui/components/NavigationBar";
import { useLocation } from "react-router-dom";
import { getConsts } from "../../utils/consts";
import { useCountry } from "src/components/Context/CountryContext";
import SquareSwitch from "./SquareSwitch";
import {
  Close,
  HelpOutlineOutlined,
  HomeOutlined,
  LocationOnOutlined,
  Logout,
  NewReleasesOutlined,
  SettingsOutlined,
  Microsoft,
  ChevronRight,
  ShoppingBag,
  AttachMoney,
} from "@mui/icons-material";
import { hasPermission } from "src/infrastructure/auth/PermissionUtils";
import { UserProfile } from "src/infrastructure/auth/UserProfile";
import Button from "@jbhi-fi/lanyard-ui/components/Button/Button";
import { UserRoles } from "src/infrastructure/auth/UserRoles";
import { useIsMobile } from "src/hooks/useIsMobile";

const StyledGridContainer = styled(Grid)<GridProps>(() => ({
  backgroundColor: "#000",
}));

const StyledGroupItem = styled("li")(() => ({
  display: "flex",
  alignItems: "center",
  padding: "16px 24px",
  fontSize: "18px",
  color: "#fff",
  cursor: "pointer",
  userSelect: "none",
  backgroundColor: "black",
  transition: "background-color 0.2s ease",
  "&:hover, &:active, &:focus": {
    backgroundColor: "black !important",
  },
  "& .group-icon": {
    marginRight: "16px",
    display: "flex",
    alignItems: "center",
    color: "#fff",
    "& svg": {
      color: "#fff",
    },
  },
  "& .group-text": {
    flexGrow: 1,
  },
  "& .chevron-icon": {
    transition: "transform 0.2s ease",
    display: "flex",
    alignItems: "center",
    color: "#fff",
    "& svg": {
      color: "#fff",
    },
  },
  "&.expanded .chevron-icon": {
    transform: "rotate(90deg)",
  },
}));

const StyledChildrenContainer = styled("div")({
  "& > li": {
    paddingLeft: "56px",
  },
});

const StyledNav = styled("nav")(() => ({
  "& ul": {
    height: "100%",
    width: "350px",
  },
  '& ul[class*="container_show_false"]': {
    transform: "translateX(-350px)",
  },
  "& ul li": {
    fontSize: "18px",
  },
  "& ul li.title": {
    fontSize: "20px",
  },
}));

type ChildItem = {
  title: string;
  path: string;
  icon?: JSX.Element;
  constName: string;
  permissionName?: string;
  hideOnMobile?: boolean;
  hideInProduction?: boolean;
};

type MenuGroup = {
  title: string;
  icon: JSX.Element;
  children: ChildItem[];
};

type MenuItem = ChildItem | MenuGroup;

function isMenuGroup(item: MenuItem): item is MenuGroup {
  return "children" in item;
}

type DrawerMenuProps = {
  isMenuOpen: boolean;
  userProfile: UserProfile;
  onMenuItemSelected: (path: string) => void;
  onDrawerClose: () => void;
  onLogout: () => void;
};

export const DrawerMenu: React.FC<DrawerMenuProps> = ({
  isMenuOpen,
  userProfile,
  onMenuItemSelected,
  onDrawerClose,
  onLogout,
}) => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { country, canSwitchCountries, changeCountry } = useCountry();
  const consts = getConsts();

  const menuStructure: MenuItem[] = useMemo(
    () => [
      {
        title: "Home",
        path: consts.routerPath.home,
        constName: consts.page.home,
        icon: <HomeOutlined fontSize="small" />,
      },
      {
        title: "Pricing",
        icon: <AttachMoney fontSize="small" />,
        children: [
          {
            title: "Was Prices",
            path: consts.routerPath.wasPrice,
            constName: consts.page.wasPrice,
            permissionName: UserRoles.PricingWasPrices,
            hideOnMobile: true,
          },
          {
            title: "Historic Pricing",
            path: consts.routerPath.historicPricing,
            constName: consts.page.historicPricing,
            permissionName: UserRoles.PricingHistoricPricing,
            hideOnMobile: true,
          },
          {
            title: "Staff Price Exceptions",
            path: consts.routerPath.staffExceptions,
            constName: consts.page.staffExceptions,
            permissionName: UserRoles.PricingStaffExceptions,
            hideOnMobile: true,
          },
        ],
      },
      {
        title: "Product",
        icon: <ShoppingBag fontSize="small" />,
        children: [
          {
            title: "Brand Images",
            path: consts.routerPath.brandImages,
            constName: consts.page.brandImages,
            permissionName: UserRoles.ProductBrandImages,
            hideOnMobile: true,
          },
          {
            title: "Product Messaging",
            path: consts.routerPath.messaging,
            constName: consts.page.messaging,
            permissionName: UserRoles.ProductMessaging,
            hideOnMobile: true,
          },
          {
            title: "Product Synchronization",
            path: consts.routerPath.productSync,
            constName: consts.page.productSync,
            permissionName: UserRoles.ProductSync,
            hideOnMobile: true,
          },
          {
            title: "The Replicator",
            path: consts.routerPath.prodToUatSkuSync,
            constName: consts.page.prodToUatSkuSync,
            permissionName: UserRoles.ProductSync,
            hideOnMobile: true,
            hideInProduction: true,
          },
          {
            title: "Product History",
            path: consts.routerPath.productHistory,
            constName: consts.page.productHistory,
            permissionName: UserRoles.ProductHistory,
            hideOnMobile: true,
          },
        ],
      },
      {
        title: "Settings",
        path: consts.routerPath.settings,
        constName: consts.page.settings,
        icon: <SettingsOutlined fontSize="small" />,
      },
      {
        title: "My Apps",
        path: "https://myapps.microsoft.com/",
        constName: "",
        icon: <Microsoft fontSize="small" />,
        hideOnMobile: true,
      },
      {
        title: "Help",
        path: consts.routerPath.help,
        constName: consts.page.help,
        icon: <HelpOutlineOutlined fontSize="small" />,
      },
      {
        title: "Release Notes",
        path: consts.routerPath.releaseNotes,
        constName: consts.page.releaseNotes,
        icon: <NewReleasesOutlined fontSize="small" />,
      },
    ],
    [consts]
  );

  const filteredMenuStructure: MenuItem[] = useMemo(() => {
    const isProduction = process.env.REACT_APP_ENVIRONMENT === "prod";
    const filterItems = (items: ChildItem[]): ChildItem[] =>
      items.filter((item) => {
        if (isMobile && item.hideOnMobile) return false;
        if (isProduction && item.hideInProduction) return false;
        if (!item.permissionName) return true;
        return hasPermission(item.permissionName, userProfile.roles);
      });

    return menuStructure
      .map((item) => {
        if (isMenuGroup(item)) {
          const filteredChildren = filterItems(item.children);
          return filteredChildren.length > 0 ? { ...item, children: filteredChildren } : null;
        }
        return filterItems([item])[0] || null;
      })
      .filter((item): item is MenuItem => item !== null);
  }, [menuStructure, isMobile, userProfile.roles]);

  const [activePath, setActivePath] = useState(location.pathname);

  useEffect(() => {
    setActivePath(location.pathname);
  }, [location.pathname]);

  const handleMenuItemSelected = useCallback(
    (item: ChildItem) => {
      setActivePath(item.path);
      if (item.path.startsWith("https")) {
        window.open(item.path);
      } else {
        onMenuItemSelected(item.path);
      }
    },
    [onMenuItemSelected]
  );

  const activeGroup = useMemo((): string | null => {
    for (const item of filteredMenuStructure) {
      if (isMenuGroup(item)) {
        const active = item.children.some((child) => child.path === activePath);
        if (active) return item.title;
      }
    }
    return null;
  }, [filteredMenuStructure, activePath]);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    setExpandedGroups(activeGroup ? new Set([activeGroup]) : new Set());
  }, [activeGroup]);

  const toggleGroup = (title: string) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(title) ? next.delete(title) : next.add(title);
      return next;
    });

  const renderMenuItem = (item: MenuItem, index: number) => {
    if (isMenuGroup(item)) {
      const expanded = expandedGroups.has(item.title);
      return (
        <div key={`group-${index}`}>
          <StyledGroupItem className={expanded ? "expanded" : ""} onClick={() => toggleGroup(item.title)}>
            <span className="group-icon">{item.icon}</span>
            <span className="group-text">{item.title}</span>
            <span className="chevron-icon">
              <ChevronRight fontSize="small" />
            </span>
          </StyledGroupItem>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <StyledChildrenContainer>
              {item.children.map((child, childIndex) => (
                <NavigationItem
                  key={`${index}-${childIndex}`}
                  text={child.title}
                  icon={child.icon}
                  isActive={activePath === child.path}
                  onSelect={() => handleMenuItemSelected(child)}
                />
              ))}
            </StyledChildrenContainer>
          </Collapse>
        </div>
      );
    } else {
      return (
        <NavigationItem
          key={`item-${index}`}
          text={item.title}
          icon={item.icon}
          isActive={activePath === item.path}
          onSelect={() => handleMenuItemSelected(item)}
        />
      );
    }
  };

  return (
    <StyledNav>
      <NavigationBar type="primary" show={isMenuOpen} onClose={onDrawerClose}>
        <StyledGridContainer container component="li" className="title" alignItems="center" paddingX={2} paddingY={1.5}>
          <Grid item height="40px">
            <Button
              type="iconOnly"
              onClick={onDrawerClose}
              icon={<Close style={{ color: "#fff", fontSize: "24px" }} />}
            />
          </Grid>
          <Grid item flexGrow={1} textAlign="center">
            Menu
          </Grid>
        </StyledGridContainer>

        {canSwitchCountries && (
          <StyledGridContainer
            container
            component="li"
            alignItems="center"
            paddingLeft={3}
            paddingRight={2}
            paddingY={1.5}
          >
            <Grid item height="20px">
              <LocationOnOutlined sx={{ color: "#fff", fontSize: "20px" }} />
            </Grid>
            <Grid item marginLeft={2} flexGrow={1}>
              Country
            </Grid>
            <Grid item>
              <SquareSwitch leftText="AU" rightText="NZ" currentCountry={country} onChange={changeCountry} />
            </Grid>
          </StyledGridContainer>
        )}

        {filteredMenuStructure.map((item, index) => renderMenuItem(item, index))}

        <NavigationFooter>
          <NavigationItem icon={<Logout />} text="Logout" onSelect={onLogout} />
        </NavigationFooter>
      </NavigationBar>
    </StyledNav>
  );
};
